import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { submitWorkWith } from "@/lib/api";

const options = [
  { id: "dog", label: "My Dog", desc: "Training, boot camp, service dog, or behavior rehab" },
  { id: "business", label: "My Business", desc: "Founder & CEO strategy, business development" },
  { id: "brand", label: "My Brand", desc: "Positioning, creative direction, campaigns" },
  { id: "team", label: "My Team", desc: "Leadership, culture, organizational transformation" },
  { id: "event", label: "My Event", desc: "Keynote, workshop, panel, or private session" },
  { id: "ai", label: "My Use of AI", desc: "Coaching, automation, prompts, workflows" },
  { id: "community", label: "My Community", desc: "Program design, workforce, social impact" },
  { id: "partnership", label: "A Partnership / Investment", desc: "Strategic partnership or venture inquiry" },
];

const budgets = ["Under $2,500", "$2,500 – $10,000", "$10,000 – $50,000", "$50,000+", "Prefer to discuss"];
const heardFrom = ["Google", "Instagram", "Facebook", "TikTok", "Referral", "Podcast / Media", "Event", "Other"];

export default function WorkWithToriano() {
  const [params, setParams] = useSearchParams();
  const preset = params.get("type");
  const [selected, setSelected] = useState(preset || null);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", city: "",
    inquiry_type: preset || "", budget: "", start_date: "",
    objective: "", heard_from: "", contact_method: "Email", message: "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    document.title = "Work With Toriano — Consulting, AI, Speaking, Partnerships";
    window.scrollTo(0, 0);
    if (preset) setSelected(preset);
  }, [preset]);

  const pick = (id) => {
    setSelected(id);
    setForm({ ...form, inquiry_type: id });
    setParams({ type: id });
    setTimeout(() => document.getElementById("work-form")?.scrollIntoView({ behavior: "smooth" }), 200);
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await submitWorkWith({ ...form, inquiry_type: selected });
      toast.success("Thank you. Your inquiry has been received. Toriano's team will review your request and contact qualified applicants.");
      setForm({ ...form, name: "", company: "", email: "", phone: "", city: "", budget: "", start_date: "", objective: "", heard_from: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-[120px] md:pt-[140px]" data-testid="work-with-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <div className="text-eyebrow text-gold mb-4">Work With Toriano</div>
        <h1 className="font-display text-[clamp(2.4rem,7vw,7rem)] leading-[0.92] tracking-[-0.03em] max-w-5xl">
          What would you like Toriano to help you <span className="italic gold-foil">transform?</span>
        </h1>
        <p className="mt-6 max-w-2xl text-bone/75 font-editorial text-lg">
          Founder-level ownership, field-tested leadership, and cultural intelligence — brought into the room for organizations, brands and individuals that need a clearer path forward.
        </p>
      </section>

      <section className="section pt-0">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {options.map((o, i) => (
              <motion.button
                key={o.id}
                onClick={() => pick(o.id)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.03 * i }}
                className={`text-left p-6 border ${selected === o.id ? "border-gold bg-gradient-to-b from-gold/5 to-transparent" : "border-hair hover:border-gold/60"} transition-colors`}
                data-testid={`work-option-${o.id}`}
              >
                <div className={`text-hair mb-2 ${selected === o.id ? "text-gold" : "text-bone/50"}`}>0{i + 1}</div>
                <div className="font-display text-2xl italic">{o.label}</div>
                <p className="mt-3 text-sm text-bone/65">{o.desc}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {selected && (
        <section id="work-form" className="section pt-0">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid md:grid-cols-12 gap-10 border-t border-hair pt-16">
              <div className="md:col-span-5">
                <div className="text-eyebrow text-gold">Qualification</div>
                <h3 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                  Let&apos;s explore <span className="italic gold-foil">{options.find(o => o.id === selected)?.label.toLowerCase()}</span> together.
                </h3>
                <p className="mt-6 text-bone/70 font-editorial">Every serious inquiry receives a hand-drafted response within 24 hours.</p>
              </div>
              <form onSubmit={submit} className="md:col-span-7 grid md:grid-cols-2 gap-6" data-testid="work-form">
                <div className="field"><label>Name</label><input required value={form.name} onChange={upd("name")} data-testid="work-name"/></div>
                <div className="field"><label>Company / Organization</label><input value={form.company} onChange={upd("company")} data-testid="work-company"/></div>
                <div className="field"><label>Email</label><input required type="email" value={form.email} onChange={upd("email")} data-testid="work-email"/></div>
                <div className="field"><label>Phone</label><input required value={form.phone} onChange={upd("phone")} data-testid="work-phone"/></div>
                <div className="field"><label>City</label><input value={form.city} onChange={upd("city")} data-testid="work-city"/></div>
                <div className="field"><label>Estimated budget</label>
                  <select value={form.budget} onChange={upd("budget")} data-testid="work-budget">
                    <option value="">Select</option>
                    {budgets.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="field"><label>Desired start date</label><input type="date" value={form.start_date} onChange={upd("start_date")} data-testid="work-date"/></div>
                <div className="field"><label>How did you hear about Toriano?</label>
                  <select value={form.heard_from} onChange={upd("heard_from")} data-testid="work-source">
                    <option value="">Select</option>
                    {heardFrom.map(h => <option key={h}>{h}</option>)}
                  </select>
                </div>
                <div className="field md:col-span-2"><label>Primary objective</label><input value={form.objective} onChange={upd("objective")} data-testid="work-objective" placeholder="What are you trying to transform?" /></div>
                <div className="field md:col-span-2"><label>Anything else Toriano should know?</label><textarea value={form.message} onChange={upd("message")} data-testid="work-message"/></div>
                <div className="field md:col-span-2"><label>Preferred contact method</label>
                  <select value={form.contact_method} onChange={upd("contact_method")} data-testid="work-contact-method">
                    <option>Email</option><option>Phone</option><option>Text</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={loading} className="btn-gold" data-testid="work-submit">
                    {loading ? "Sending…" : "Submit Inquiry"}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
